import { openDB, type IDBPDatabase } from 'idb';

/**
 * Lightweight IndexedDB queue for one-time expense inserts that happen
 * while the device is offline, plus a draft store for in-flight scanned
 * receipts so users don't lose AI-extracted data if the WebView is killed
 * (phone call on iOS, force-back gesture, OS memory pressure, etc.).
 *
 * On reconnect the expense queue is replayed through the normal Supabase
 * insert path. Recurring templates, deletes, edits, and goal contributions
 * are intentionally NOT queued - they touch server-side state that cannot
 * be safely replayed without conflict logic.
 */

const DB_NAME = 'fennest-offline';
const DB_VERSION = 3;
const STORE = 'pending_expenses';
const DRAFT_STORE = 'scan_drafts';

/** Hard cap on the stored receipt blob (post-downscale) to keep IndexedDB usage bounded. */
const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
/** Skip storing the full image if free storage is below this threshold. */
const MIN_FREE_QUOTA_BYTES = 40 * 1024 * 1024;

const MAX_ATTEMPTS = 5;
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export interface PendingExpense {
  id: string;                 // client UUID, doubles as Supabase row id for idempotency
  user_id: string;
  amount: number;
  description: string;
  category: string;
  expense_date: string;       // yyyy-MM-dd
  source_type: string;
  vendor?: string;
  receipt_data?: unknown;
  queued_at: number;          // ms epoch
  attempt_count?: number;
  last_error?: string;
  blocked?: boolean;
}

export type DraftImageQuality = 'high' | 'medium' | 'low' | 'thumbnail-only';

export interface ScanDraft {
  id: string;
  user_id: string;
  ocr_result: unknown;        // full OCRResult JSON
  thumbnail_data_url?: string; // small preview so the user recognizes the receipt
  image_blob?: Blob;           // full receipt image (downscaled JPEG) for fullscreen review
  image_mime?: string;
  image_quality?: DraftImageQuality; // signals review UI when only the thumbnail survived
  created_at: number;
  expires_at: number;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb() {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB not available'));
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(DRAFT_STORE)) {
          db.createObjectStore(DRAFT_STORE, { keyPath: 'id' });
        }
      },
    });
  }
  return dbPromise;
}

export async function enqueueExpense(entry: PendingExpense): Promise<void> {
  const db = await getDb();
  await db.put(STORE, { attempt_count: 0, blocked: false, ...entry });
}

export async function getPendingExpenses(): Promise<PendingExpense[]> {
  try {
    const db = await getDb();
    return await db.getAll(STORE);
  } catch {
    return [];
  }
}

/** Returns only entries that are NOT blocked (still eligible for auto-replay). */
export async function getReplayableExpenses(): Promise<PendingExpense[]> {
  const all = await getPendingExpenses();
  return all.filter((e) => !e.blocked);
}

export async function getBlockedExpenses(): Promise<PendingExpense[]> {
  const all = await getPendingExpenses();
  return all.filter((e) => e.blocked);
}

export async function getPendingCount(): Promise<number> {
  try {
    const db = await getDb();
    return await db.count(STORE);
  } catch {
    return 0;
  }
}

export async function removePendingExpense(id: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, id);
}

/**
 * Record a failed replay attempt. After MAX_ATTEMPTS the entry is marked
 * blocked so the auto-replay loop stops touching it; the user is then
 * prompted to review/retry/discard via the offline pill.
 */
export async function recordReplayFailure(id: string, errorMsg: string): Promise<void> {
  try {
    const db = await getDb();
    const existing = (await db.get(STORE, id)) as PendingExpense | undefined;
    if (!existing) return;
    const nextCount = (existing.attempt_count || 0) + 1;
    const updated: PendingExpense = {
      ...existing,
      attempt_count: nextCount,
      last_error: errorMsg.slice(0, 500),
      blocked: nextCount >= MAX_ATTEMPTS,
    };
    await db.put(STORE, updated);
  } catch (err) {
    console.warn('[offlineQueue] recordReplayFailure failed:', err);
  }
}

/** Manually retry a blocked entry - resets the counter so the next reconnect picks it up. */
export async function unblockPendingExpense(id: string): Promise<void> {
  try {
    const db = await getDb();
    const existing = (await db.get(STORE, id)) as PendingExpense | undefined;
    if (!existing) return;
    await db.put(STORE, { ...existing, attempt_count: 0, blocked: false, last_error: undefined });
  } catch (err) {
    console.warn('[offlineQueue] unblockPendingExpense failed:', err);
  }
}

export async function clearAllPending(): Promise<void> {
  const db = await getDb();
  await db.clear(STORE);
}

// ─── Scan drafts ─────────────────────────────────────────────────────────

export async function saveScanDraft(draft: Omit<ScanDraft, 'created_at' | 'expires_at'> & {
  created_at?: number;
  expires_at?: number;
}): Promise<void> {
  try {
    const db = await getDb();
    const now = Date.now();
    const record: ScanDraft = {
      ...draft,
      created_at: draft.created_at ?? now,
      expires_at: draft.expires_at ?? now + DRAFT_TTL_MS,
    };
    await db.put(DRAFT_STORE, record);
  } catch (err) {
    // Drafts are best-effort. Never block the scan flow on storage errors.
    console.warn('[offlineQueue] saveScanDraft failed:', err);
  }
}

export async function getLatestScanDraft(userId: string): Promise<ScanDraft | null> {
  try {
    const db = await getDb();
    const all = (await db.getAll(DRAFT_STORE)) as ScanDraft[];
    const now = Date.now();
    // Drop expired drafts on the way past.
    for (const d of all) {
      if (d.expires_at < now) {
        await db.delete(DRAFT_STORE, d.id).catch(() => {});
      }
    }
    const fresh = all
      .filter((d) => d.user_id === userId && d.expires_at >= now)
      .sort((a, b) => b.created_at - a.created_at);
    return fresh[0] ?? null;
  } catch {
    return null;
  }
}

export async function clearScanDraft(id: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete(DRAFT_STORE, id);
  } catch {
    // ignore
  }
}

/** Wipe every cached scan draft (used on sign-out so the next account on a shared device sees nothing). */
export async function wipeAllScanDrafts(): Promise<void> {
  try {
    const db = await getDb();
    await db.clear(DRAFT_STORE);
  } catch {
    // ignore
  }
}

/**
 * Downscale an image Blob/File to a JPEG with max edge `maxEdge` and quality `quality`.
 * Returns null if the browser can't run the canvas pipeline.
 *
 * Defaults are tuned for receipt legibility: 2400px / Q0.88 keeps small line-item text
 * readable in the resume preview without blowing past IndexedDB caps.
 */
export async function downscaleImageForDraft(
  source: Blob | File,
  maxEdge = 2400,
  quality = 0.88,
): Promise<Blob | null> {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return null;
  return new Promise<Blob | null>((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(source);
    img.onload = () => {
      try {
        URL.revokeObjectURL(url);
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (w > maxEdge || h > maxEdge) {
          const scale = maxEdge / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/**
 * Read the natural dimensions of an image so callers can choose tier-appropriate
 * downscale parameters (long receipts deserve more pixels on the long edge).
 */
async function getImageNaturalSize(source: Blob | File): Promise<{ w: number; h: number } | null> {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return null;
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(source);
    img.onload = () => {
      const out = { w: img.naturalWidth, h: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(out);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/**
 * Produce the best on-device draft image we can fit under MAX_IMAGE_BYTES.
 *
 * Tries high → medium → low quality tiers, scaling the long edge based on
 * receipt aspect ratio (very long bills get more pixels). Returns the blob
 * plus a tag so the review UI can warn the user when only a thumbnail would fit.
 */
export interface DraftImageBuildResult {
  blob: Blob | null;
  mime: string;
  quality: DraftImageQuality;
}

export async function buildDraftImage(source: Blob | File): Promise<DraftImageBuildResult> {
  const dims = await getImageNaturalSize(source);
  const longEdge = dims ? Math.max(dims.w, dims.h) : 0;
  const shortEdge = dims ? Math.min(dims.w, dims.h) : 1;
  const ratio = shortEdge > 0 ? longEdge / shortEdge : 1;

  // Aspect-aware ceilings - long receipts get more pixels on the long edge.
  const highMax = ratio > 2.5 ? 3000 : 2400;

  const tiers: Array<{ q: DraftImageQuality; max: number; jpeg: number }> = [
    { q: 'high', max: highMax, jpeg: 0.88 },
    { q: 'medium', max: 2000, jpeg: 0.85 },
    { q: 'low', max: 1600, jpeg: 0.80 },
  ];

  for (const tier of tiers) {
    try {
      const blob = await downscaleImageForDraft(source, tier.max, tier.jpeg);
      if (blob && blob.size <= MAX_IMAGE_BYTES) {
        return { blob, mime: 'image/jpeg', quality: tier.q };
      }
    } catch {
      // try next tier
    }
  }
  return { blob: null, mime: 'image/jpeg', quality: 'thumbnail-only' };
}

/** Check rough storage headroom; returns true if it's safe to store a ~MAX_IMAGE_BYTES blob. */
export async function hasStorageHeadroom(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return true;
    const est = await navigator.storage.estimate();
    if (typeof est.quota === 'number' && typeof est.usage === 'number') {
      return (est.quota - est.usage) > MIN_FREE_QUOTA_BYTES;
    }
    return true;
  } catch {
    return true;
  }
}

export const SCAN_IMAGE_MAX_BYTES = MAX_IMAGE_BYTES;

export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}
